import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Alert, Platform } from 'react-native';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { SaveFormat } from 'expo-image-manipulator';

import * as ImagePicker from 'expo-image-picker';
import Toast from 'react-native-root-toast';

import { s3upload } from '../utils/fileUpload';
import { Spinner } from '../components';
import { useAppContext } from '../../common/utils/context';
import { useOfflineQueue } from '../offline';
import { useTheme } from '../theme';

const withMediaPicker = WrappedComponent => ({
	s3Path = 'images/machines/',
	quickMediaCapture,
	allowMultipleSelection = false,
	allowMultipleCapture = false,
	promptForAnotherCapture = true,
	selectionLimit = 0,
	collectCapturedMediaBatch = false,
	fallbackToLocalUri = false,
	...passThroughProps
}) => {
	const { showActionSheetWithOptions } = useActionSheet();
	const { theme } = useTheme();
	const [processing, setProcessing] = useState(false);

	const [loadedMediaUri, setLoadedMediaUri] = useState(null);
	const [capturedMediaLink, setCapturedMediaLink] = useState(null);
	const [capturedMediaBatch, setCapturedMediaBatch] = useState(null);
	const capturedMediaBatchIdRef = useRef(0);
	const { getServerEndpoint } = useAppContext();
	const { isOnline } = useOfflineQueue();
	const isOnlineRef = useRef(isOnline);
	isOnlineRef.current = isOnline;
	// Use the current expo-image-picker media type format.
	const imagePickerOptions = useMemo(() => ({ mediaTypes: ['images'] }), []);
	const actionSheetStyles = useMemo(() => ({
		containerStyle: {
			backgroundColor: theme.mainBg18 || theme.mainBg1,
		},
		textStyle: {
			color: theme.text1,
		},
		titleTextStyle: {
			color: theme.text1,
		},
		separatorStyle: {
			backgroundColor: theme.border1 || theme.mainBg3,
		},
		tintColor: theme.text1,
	}), [theme]);

	const uploadImgToServer = useCallback(async (data) => {
		if (!data) {
			return null;
		}

		if (!isOnlineRef.current) {
			return data?.uri || null;
		}

		try {
			const imageLink = await s3upload(data, null, s3Path, getServerEndpoint, loadedMediaUri, SaveFormat.JPEG);
			if (imageLink) {
				return imageLink;
			}
			if (fallbackToLocalUri) {
				return data?.uri || null;
			}

			Toast.show('Error when uploading the photo');
			return null;
		}
		catch (e) {
			if (fallbackToLocalUri) {
				return data?.uri || null;
			}

			Toast.show('Error when uploading the photo');
			return null;
		}
	}, [fallbackToLocalUri, getServerEndpoint, loadedMediaUri, s3Path]);

	const uploadAssetsToServer = useCallback(async (assets) => {
		if (!assets?.length) {
			return;
		}

		setProcessing(true);
		try {
			const mediaLinks = [];

			// Upload sequentially to keep memory usage stable for multi-select.
			for (const asset of assets) {
				const imageLink = await uploadImgToServer(asset);
				if (imageLink) {
					mediaLinks.push(imageLink);
					setCapturedMediaLink(imageLink);
				}
			}

			if (collectCapturedMediaBatch && mediaLinks.length) {
				capturedMediaBatchIdRef.current += 1;
				setCapturedMediaBatch({
					id: capturedMediaBatchIdRef.current,
					links: mediaLinks,
				});
			}
		}
		finally {
			setProcessing(false);
		}
	}, [collectCapturedMediaBatch, uploadImgToServer]);

	const normalizeAssets = useCallback((result) => {
		if (!result) {
			return [];
		}

		if (Array.isArray(result?.assets)) {
			return result.assets;
		}

		if (Array.isArray(result?.selected)) {
			return result.selected;
		}

		return result?.uri ? [result] : [];
	}, []);

	const filterImageAssets = useCallback((assets) => {
		if (!assets?.length) {
			return [];
		}

		return assets.filter(asset => (asset?.type ? asset.type === 'image' : true));
	}, []);

	const promptForAnotherPhoto = useCallback((title, confirmText) => {
		return new Promise(resolve => {
			Alert.alert(
				title,
				'',
				[
					{ text: 'Done', onPress: () => resolve(false) },
					{ text: confirmText, onPress: () => resolve(true) },
				],
				{
					cancelable: true,
					onDismiss: () => resolve(false),
				},
			);
		});
	}, []);

	const captureMultipleFromCamera = useCallback(async (options = {}) => {
		const assets = [];
		let shouldContinue = true;
		const { selectionLimit: captureLimit, ...cameraOptions } = options;

		while (shouldContinue) {
			const result = await ImagePicker.launchCameraAsync(cameraOptions);

			if (result?.canceled || result?.cancelled) {
				break;
			}

			const nextAssets = filterImageAssets(normalizeAssets(result));
			if (!nextAssets.length) {
				Toast.show('Unable to read the photo');
				break;
			}

			assets.push(...nextAssets);
			if (captureLimit > 0 && assets.length >= captureLimit) {
				break;
			}

			if (!promptForAnotherCapture) {
				shouldContinue = false;
				continue;
			}

			// Ask after each capture so users can stop without reopening the flow.
			shouldContinue = await promptForAnotherPhoto('Take another photo?', 'Take another photo');
		}

		return assets.length ? { assets } : { canceled: true };
	}, [filterImageAssets, normalizeAssets, promptForAnotherPhoto, promptForAnotherCapture]);

	const captureMultipleFromLibrary = useCallback(async (options = {}) => {
		const assets = [];
		let shouldContinue = true;

		while (shouldContinue) {
			const remaining = options.selectionLimit > 0 ? options.selectionLimit - assets.length : 0;
			const result = await ImagePicker.launchImageLibraryAsync({
				...options,
				selectionLimit: remaining,
			});

			if (result?.canceled || result?.cancelled) {
				break;
			}

			const nextAssets = filterImageAssets(normalizeAssets(result));
			if (!nextAssets.length) {
				Toast.show('Unable to read the photo');
				break;
			}

			assets.push(...nextAssets);
			if (options.selectionLimit > 0 && assets.length >= options.selectionLimit) {
				break;
			}

			if (!allowMultipleSelection) {
				break;
			}

			if (Array.isArray(result?.assets) && result.assets.length > 1) {
				break;
			}

			shouldContinue = await promptForAnotherPhoto('Add another photo?', 'Choose another photo');
		}

		return assets.length ? { assets } : { canceled: true };
	}, [allowMultipleSelection, filterImageAssets, normalizeAssets, promptForAnotherPhoto]);

	const processPhoto = useCallback(async (getPhotoFunction, options = {}) => {
		// clean previous media Uri
		setLoadedMediaUri(null);

		const { status: cameraPermissionStatus } = await ImagePicker.requestCameraPermissionsAsync();

		if (cameraPermissionStatus !== 'granted') {
			alert('Sorry, we need the permission to make this work.');
			return;
		}

		if (Platform.OS === 'ios') {
			const { status: cameraRollPermissionStatus } = await ImagePicker.requestMediaLibraryPermissionsAsync();
			if (cameraRollPermissionStatus !== 'granted') {
				alert('Sorry, we need the permission to make this work.');
				return;
			}
		}

		let result = null;
		try {
			result = await getPhotoFunction(options);
		}
		catch (error) {
			Toast.show('Unable to open the photo picker');
			return;
		}

		if (result?.canceled || result?.cancelled) {
			return;
		}

		const assets = filterImageAssets(normalizeAssets(result));
		if (!assets.length) {
			Toast.show('Unable to read the photo');
			return;
		}

		await uploadAssetsToServer(options.selectionLimit > 0
			? assets.slice(0, options.selectionLimit)
			: assets);
	}, [filterImageAssets, normalizeAssets, uploadAssetsToServer]);

	const captureMedia = useCallback(async ({ selectionLimit: selectionLimitOverride, quickMediaCapture: quickMediaCaptureOverride = quickMediaCapture } = {}) => {
		const activeSelectionLimit = selectionLimitOverride ?? selectionLimit;
		if (quickMediaCaptureOverride) {
			const cameraCapture = allowMultipleCapture ? captureMultipleFromCamera : ImagePicker.launchCameraAsync;
			return processPhoto(cameraCapture, {
				...imagePickerOptions,
				...(allowMultipleCapture ? { selectionLimit: activeSelectionLimit } : {}),
			});
		}
		else {
			showActionSheetWithOptions(
				{
					options: ['Take photo', 'Choose from Library', 'Cancel'],
					cancelButtonIndex: 2,
					...actionSheetStyles,
				},
				async buttonIndex => {
					// cancel
					if (buttonIndex === 2) {
						return;
					}

					if (buttonIndex === 0) {
						const cameraCapture = allowMultipleCapture ? captureMultipleFromCamera : ImagePicker.launchCameraAsync;
						await processPhoto(cameraCapture, {
							...imagePickerOptions,
							...(allowMultipleCapture ? { selectionLimit: activeSelectionLimit } : {}),
						});
						return;
					}

					const libraryCapture = allowMultipleSelection ? captureMultipleFromLibrary : ImagePicker.launchImageLibraryAsync;
					await processPhoto(libraryCapture, {
						...imagePickerOptions,
						allowsMultipleSelection: allowMultipleSelection,
						selectionLimit: allowMultipleSelection ? activeSelectionLimit : 1,
					});
				},
			);
		}

	}, [processPhoto, showActionSheetWithOptions, quickMediaCapture, actionSheetStyles, allowMultipleSelection, allowMultipleCapture, captureMultipleFromCamera, captureMultipleFromLibrary, selectionLimit, imagePickerOptions]);

	return (
		<>
			{
				processing && (
					<Spinner isOverlay={true} />
				)
			}
			<WrappedComponent
				captureMedia={captureMedia}
				capturedMediaLink={capturedMediaLink}
				capturedMediaBatch={capturedMediaBatch}
				processingMedia={processing}
				{...passThroughProps}
			/>
		</>
	);
};

export default withMediaPicker;
